import {
  AccountInfoProps,
  UserAccountInfo,
  UserAccountType
} from 'typings/userInfo'
import { clientDetail, clientInfo } from 'configureBrand'
import { CardType, PrivilegeModule } from 'typings/account'

const { payableAccount, payableAccountBRC } = clientDetail
const { clientName, privileges } = clientInfo

export const userAccountInfo: UserAccountInfo = {
  /**
   * type of account
   */
  type: 'prox',
  /**
   * type of product
   */
  productType: 'generic',
  /**
   * string value to determine the privilege of logged in user
   */
  securityAccess: '',
  /**
   * type of card of the logged in account
   */
  cardType: '',
  /**
   * NOTE: not being used anywhere
   */
  email: '',
  /**
   * account details of the logged in account
   */
  accountInfo: {
    'cipher.accountId': '',
    'cipher.partialAccountId': '',
    switchAccountType: 'SELF'
  },
  /**
   * accountIdType will always be 'guid' except when app loads
   * TODO:Remove the code where accoundIdType is being updated to empty string('')
   */
  accountIdType: '',
  /**
   * stateful value of the accountID
   */
  accountID: '',
  /**
   * This is a unique identifier used for tracking a user session throughout the
   * stack. It is used as the correlation id for api requests and other messages
   * between UI and services.
   */
  trackingID: '',
  /**
   * @returns type of account
   */
  get accountType(): UserAccountType {
    return this.type
  },
  /**
   * Sets the account type and product type - generic | prox | brc
   * @param type stateful value for the type of account
   */
  set accountType(type: UserAccountType) {
    this.type = type
    this.productType = type
  },
  /**
   * Sets the product type for the analytics
   * @param analyticsType type of account
   */
  set analyticsProductType(analyticsType: UserAccountType) {
    this.productType = analyticsType
  },
  /**
   * @returns product type
   */
  get analyticsProductType(): UserAccountType {
    return this.productType
  },
  /**
   * Sets the securityLevel string to determine the privileges of the logged in user
   * @param securityAccess stateful string value to determine the privileges
   */
  set securityLevel(securityAccess: string) {
    this.securityAccess = securityAccess
  },
  /**
   * Determines if the logged in account is Prox
   * @returns true if Prox
   */
  get isProx(): boolean {
    return this.type === 'prox'
  },
  /**
   * @returns account Info
   */
  get accountDetail(): AccountInfoProps {
    return this.accountInfo
  },
  /**
   * sets the account details of the logged in account
   * @param props stateful object value to update the logged in account details
   */
  set accountDetail(props: AccountInfoProps) {
    this.accountInfo = { ...this.accountInfo, ...props }
  },
  /**
   * Used to set the trackingID
   * @param trackingID value generated from the @function generateTrackingID
   */
  set trackingInfo(trackingID: string) {
    this.trackingID = trackingID
  },
  /**
   * @returns trackingID value
   */
  get trackingInfo(): string {
    return this.trackingID
  },
  /**
   * @returns payable account name based on the account type
   */
  get payableAccount(): string {
    return this.isProx ? payableAccount : payableAccountBRC
  }
}

/**
 * @param moduleType type of module
 * @returns account details object for the api calls
 */
export const getAccountDetail = (moduleType: string): AccountInfoProps => {
  const { accountID } = userAccountInfo

  switch (moduleType) {
    case 'PAYMENT':
      return {
        accountIdType: 'guid'
      }
    case 'USER':
      return {
        'cipher.accountId': accountID,
        accountIdType: 'guid'
      }
    default:
      return { accountIdType: 'guid' }
  }
}

/**
 * Used to get the index of module
 * @param moduleName name of module to determine the index
 * @param client name of client
 * @returns the index of the module
 */
export const getModuleIndex = (
  moduleName: keyof PrivilegeModule,
  client: string
): number => {
  const { isProx, cardType } = userAccountInfo
  if (client === 'sams' && cardType === 'dualcard') {
    return privileges?.dc?.[moduleName]
  }
  return isProx ? privileges.prox[moduleName] : privileges.brc[moduleName]
}

/**
 * Checks if client has permission to a specific module
 * @param moduleName name of the module
 * @returns true if index of the module is undefined
 * @returns true if index value is '1' and false if index vlaue is '0'
 */
export const hasModulePermission = (moduleName: string): boolean => {
  const key = moduleName as keyof PrivilegeModule
  const index = getModuleIndex(key, clientName)
  if (index === undefined) {
    return true
  }
  const accessList = userAccountInfo.securityAccess.split('')
  return accessList[index] === '1'
}

/**
 * Used to get the card type - 'prox' | 'plcc' | 'brc' | 'dualcard' | ''
 * @returns stateful cardType value
 */
export const getCardType = (): CardType => {
  const { cardType } = userAccountInfo
  return cardType || ''
}
